import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, apiFetchRaw } from './client.js';
import type { Membership } from './types.js';

export interface UpsertMemberPayload {
  tenantId: string;
  email: string;
  role: Membership['role'];
  expiresAt?: string | null;
}

export interface UpsertMemberResult {
  membership: Membership;
  status: 200 | 201;
}

export function useUpsertMember() {
  const qc = useQueryClient();
  return useMutation<UpsertMemberResult, ApiError, UpsertMemberPayload>({
    mutationFn: async (payload) => {
      const res = await apiFetchRaw('/admin/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let problem: unknown = null;
        try {
          problem = await res.json();
        } catch {
          /* not JSON */
        }
        throw new ApiError(res.status, problem);
      }
      const membership = (await res.json()) as Membership;
      return { membership, status: res.status === 201 ? 201 : 200 };
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tenant-members', vars.tenantId] });
    },
  });
}

export interface ChangeRolePayload {
  uid: string;
  tenantId: string;
  role: Membership['role'];
}

export function useChangeRole() {
  const qc = useQueryClient();
  return useMutation<Membership, ApiError, ChangeRolePayload>({
    mutationFn: ({ uid, tenantId, role }) =>
      apiFetch<Membership>(
        `/admin/members/${encodeURIComponent(uid)}/${encodeURIComponent(tenantId)}/role`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tenant-members', vars.tenantId] });
    },
  });
}

export interface RevokeMemberPayload {
  uid: string;
  tenantId: string;
}

export function useRevokeMember() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, RevokeMemberPayload>({
    mutationFn: ({ uid, tenantId }) =>
      apiFetch<void>(`/admin/members/${encodeURIComponent(uid)}/${encodeURIComponent(tenantId)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tenant-members', vars.tenantId] });
    },
  });
}
